import { useRef, useState } from 'react'
import { useIntl } from 'react-intl'

export type MimeTypes = 'jpeg' | 'png' | 'pdf' | 'jpg' // Add more MIME types as needed

export interface UrlNameInterface {
  url: string
  name: string
}

interface UseFileUploadProps {
  allowedFileTypes: MimeTypes[]
  multiple?: boolean
  files: (File | UrlNameInterface)[]
  setFiles: (newFiles: (File | UrlNameInterface)[]) => void
}

// Type guard to check if an item is a File
export const isFile = (item: File | UrlNameInterface): item is File => {
  return (item as File).type !== undefined
}

export const useUpload = ({
  allowedFileTypes,
  multiple = true,
  files,
  setFiles,
}: UseFileUploadProps) => {
  const intl = useIntl()
  const [isDragging, setIsDragging] = useState(false)
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const decodeFilenamesFromUrls = (
    fileData: { url: string; filename?: string }[],
  ): { url: string; filename: string }[] => {
    return fileData.map(({ url, filename }) => {
      if (filename) {
        return { url, filename }
      }
      const fileNameRaw =
        new URL(url).pathname.split('/').at(-1) ?? 'Pas de fichier'
      const decodedFileName = decodeURIComponent(fileNameRaw)
      return { url, filename: decodedFileName }
    })
  }

  const formatAllowedFileTypes = () =>
    allowedFileTypes.join(', ').replace(/, ([^,]*)$/, ' ou $1')

  const handleFileChange = (
    filesToHandle: FileList | UrlNameInterface[] | null,
  ) => {
    setUploadErrors([])

    if (!filesToHandle) {
      return
    }

    let validFiles: (File | UrlNameInterface)[] = []
    const currentErrors: string[] = []

    if (!multiple && filesToHandle.length > 1) {
      currentErrors.push(
        intl.formatMessage({
          id: 'ui.useUpload.singleFileError',
          defaultMessage: `Attention, vous ne pouvez uploader qu’un seul document.`,
        }),
      )
    }

    for (let i = 0; i < filesToHandle.length; i++) {
      const file = filesToHandle[i]
      const isDuplicate = files?.some(
        (selectedFile) => selectedFile.name === file.name,
      )

      if (isDuplicate) {
        currentErrors.push(
          intl.formatMessage({
            id: 'ui.useUpload.duplicateFile',
            defaultMessage: `Le fichier ${file.name} a déjà été téléchargé.`,
          }),
        )
        continue
      }

      // Only check type for File objects
      if (isFile(file)) {
        const isValidFileType = allowedFileTypes.some((type) =>
          file.type.includes(type),
        )
        if (isValidFileType) {
          validFiles.push(file)
        } else {
          currentErrors.push(
            intl.formatMessage({
              id: 'ui.useUpload.invalidFileType',
              defaultMessage: `Le fichier ${file.name} n'est pas du bon type. Seuls les fichiers ${formatAllowedFileTypes()} sont acceptés.`,
            }),
          )
        }
      } else {
        // If it's a UrlNameType, it's already valid
        validFiles.push(file)
      }
    }

    if (!multiple) {
      validFiles = validFiles.slice(0, 1)
      setFiles(validFiles)
    } else {
      setFiles([...files, ...validFiles])
    }

    if (currentErrors.length > 0) {
      setUploadErrors(currentErrors)
    }
  }

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index)
    setFiles(updatedFiles)
    setUploadErrors([])
  }

  const handleChooseFileClick = () => {
    fileInputRef.current?.click()
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const files = event.dataTransfer?.files
    handleFileChange(files)
  }

  return {
    decodeFilenamesFromUrls,
    selectedFiles: files,
    uploadErrors,
    fileInputRef,
    handleFileChange,
    handleRemoveFile,
    handleChooseFileClick,
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  }
}
